import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <main data-testid="blog-page" className="pt-20">
      <section className="relative py-24 lg:py-32 overflow-hidden grid-bg">
        <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] bg-cyan-400/10 blur-[140px] rounded-full" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <MaskedLine delay={0.1}>
            <Overline>The Journal</Overline>
          </MaskedLine>
          <h1 className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl mt-6">
            <MaskedLine delay={0.25}>Sharp thinking,</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-stroke">shipped weekly</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="pb-24 lg:pb-32 bg-white text-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          {loading && (
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-zinc-500" data-testid="blog-loading">
              Loading articles…
            </p>
          )}
          {!loading && posts.length === 0 && (
            <p className="text-zinc-600" data-testid="blog-empty">No articles yet — check back soon.</p>
          )}

          {featured && (
            <Reveal>
              <Link
                to={`/blog/${featured.slug}`}
                data-testid="blog-featured-post"
                className="group block border border-zinc-200 rounded-2xl overflow-hidden hover:border-cyan-500 transition-colors duration-500 mb-6"
              >
                {featured.image && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={featured.image}
                      alt={featured.title}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-8 lg:p-14 flex flex-col justify-between gap-10">
                  <div>
                    <div className="flex items-center gap-4 font-mono-accent text-[10px] uppercase tracking-[0.25em]">
                      <span className="text-cyan-600">{featured.category}</span>
                      <span className="text-zinc-500">{formatDate(featured.date)}</span>
                      <span className="text-zinc-500">{featured.read_time}</span>
                    </div>
                    <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-6 group-hover:text-cyan-600 transition-colors leading-tight">
                      {featured.title}
                    </h2>
                    <p className="text-zinc-600 text-sm sm:text-base leading-relaxed mt-5">
                      {featured.excerpt}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-[0.25em] text-cyan-600">
                    Read article <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="grid md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200 mt-0">
            {rest.map((post, i) => (
              <Reveal key={post.slug} delay={i * 0.08}>
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="group block bg-white h-full overflow-hidden hover:bg-zinc-50 transition-colors duration-500"
                >
                  {post.image && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-8 lg:p-10">
                  <div className="flex items-center gap-4 font-mono-accent text-[10px] uppercase tracking-[0.25em]">
                    <span className="text-cyan-600">{post.category}</span>
                    <span className="text-zinc-500">{formatDate(post.date)}</span>
                  </div>
                  <h3 className="font-display font-bold uppercase tracking-tight text-xl lg:text-2xl mt-5 group-hover:text-cyan-600 transition-colors leading-tight">
                    {post.title}
                  </h3>
                  <p className="text-zinc-600 text-sm leading-relaxed mt-4">{post.excerpt}</p>
                  <span className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-[0.25em] text-zinc-500 group-hover:text-cyan-600 transition-colors mt-8">
                    Read <ArrowRight className="w-4 h-4" />
                  </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Blog;
